import React, { FC, useState } from 'react';
import Button, { ButtonProps } from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { IProfilePictureDTO } from 'src/api/dtos';
import { ZoomPanImage } from 'src/shared/components';
import { CameraOutlineIcon } from 'src/shared/components/icons';
import { useTranslate } from 'src/shared/providers';

interface Props extends Partial<Pick<HTMLButtonElement, 'id' | 'className'>> {
  initials: string | undefined;
  picture: IProfilePictureDTO | undefined | null;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  readOnly?: boolean;
  size?: number;
  fontSize?: number | string;
  showInnerBorder?: boolean;
  showHover?: boolean;
  sx?: Omit<ButtonProps['sx'], 'width' | 'height' | 'minHeight' | 'maxHeight'>;
}

export const EditableAvatar: FC<Props> = ({
  initials,
  picture,
  readOnly,
  onClick,
  size = 50,
  fontSize = '1.25rem',
  showInnerBorder,
  showHover,
  sx = {},
  ...props
}) => {
  const { t } = useTranslate();

  const [hover, setHover] = useState(false);

  const handleEnter = () => {
    setHover(true);
  };

  const handleLeave = () => {
    setHover(false);
  };

  return (
    <Button
      {...props}
      variant="text"
      sx={{
        position: 'relative',
        width: size,
        height: size,
        minWidth: size,
        minHeight: size,
        borderRadius: '50%',
        bgcolor: (theme) =>
          theme.palette.mode === 'dark' ? theme.palette.background.paper : theme.palette.secondary.main,
        flexShrink: 0,
        padding: 0,
        ...sx,
      }}
      onClick={onClick}
      onPointerEnter={handleEnter}
      onPointerLeave={handleLeave}
      disabled={readOnly}
    >
      {showHover && !readOnly && hover && (
        <Stack
          direction="column"
          alignItems="center"
          justifyContent="center"
          gap={1}
          position="absolute"
          top="50%"
          left="50%"
          width="100%"
          height="100%"
          borderRadius="50%"
          bgcolor={(theme) =>
            theme.palette.mode === 'dark' ? `${theme.palette.grey[800]}80` : `${theme.palette.grey[100]}80`
          }
          sx={{ transform: 'translate(-50%, -50%)' }}
        >
          <CameraOutlineIcon />
          <Typography variant="subtitle2">{t('files.uploadImage')}</Typography>
        </Stack>
      )}
      <Stack
        justifyContent="center"
        alignItems="center"
        width="100%"
        height="100%"
        sx={(theme) => ({
          borderRadius: '50%',
          ...(showInnerBorder ? { borderWidth: 4, borderStyle: 'solid', borderColor: theme.palette.primary.main } : {}),
        })}
      >
        {picture ? (
          <ZoomPanImage
            imageSrc={picture.pictureUri}
            scale={picture.scale ?? undefined}
            position={{ x: picture?.positionX ?? 0, y: picture?.positionY ?? 0 }}
            borderRadius="50%"
            disabled
          />
        ) : (
          <Typography fontSize={fontSize} color="primary">
            {initials}
          </Typography>
        )}
      </Stack>
    </Button>
  );
};
